import { documentSchema } from './IDocument';
import { ILegalRequirementDeadline } from './ILegalRequirement_deadlines';
import { z } from 'zod';
import type { ILegalRequirementTemplate } from './ILegalRequirementTemplate';
import { taskAutomationSchema } from './ITaskAutomation';
import { knowledgeItemSchema } from './IKnowledgeItem';

export const legalRequirementSchema = z.object({
  idLegalRequirement: z.number(),
  idLegalPhase: z.number(),
  LegalRequirement: z.string(),
  canUploadFilesClient: z.boolean(),
  canUploadFilesEmployees: z.boolean(),
  enableError: z.boolean(),
  enableFreeTextError: z.boolean(),
  sendMail: z.boolean(),
  EmailSubject: z.string().nullable(),
  EmailBody: z.string().nullable(),
  TextOfError: z.string().nullable(),
  hasError: z.boolean(),
  isComplete: z.boolean(),
  Position: z.number(),
  idLegalRequirementState: z.number(),
  User_idUser: z.number().nullable(),
  InfoText: z.string().nullable(),
  DocumentArrayClient: z.array(documentSchema).optional(),
  DocumentArrayEmployees: z.array(documentSchema).optional(),
  LegalRequirementState: z.string().optional(),
  Employee_idUser: z.number().optional(),
  Notary_idUser: z.number().optional(),
  idUserGroup: z.number().optional().nullable(),
  idLegalTransactionState: z.number().optional(),
  idLegalTransactionProgress: z.number().optional(),
  idLegalTransaction: z.number().optional(),
  Title: z.string().optional(),
  Closed_at: z.string().optional(),
  idCourt: z.number().optional(),
  idCourtRole: z.number().optional(),
  Court: z.string().optional(),
  CourtRole: z.string().optional(),
  // Vom Backend ergänzt: konkretes Gericht, abgeleitet aus CourtRole + effektiver
  // Gemeinde/Gemarkung des Vorgangs (oder Phase-Override).
  resolvedCourt: z
    .object({
      idCourt: z.number().nullable().optional(),
      Court: z.string().nullable().optional(),
      idAssignment: z.number().nullable().optional(),
      idAdministrativeUnit: z.number().nullable().optional(),
      fromPhase: z.boolean().optional(),
      reason: z
        .enum(['ok', 'no-court-role', 'no-admin-unit', 'no-assignment'])
        .optional(),
    })
    .nullable()
    .optional(),
  initialProcessingTime: z.number(),
  actualProcessingTime: z.number().optional(),
  DeadlineFullfilledAt: z.string().optional(),
  idLegalRequirementTemplate: z.number().optional(),
  ManualProcessingTime: z.boolean(),
  UserDeadlineNotified: z.boolean().optional(),
  isInterneLegalRequirement: z.boolean(),
  sendInternLegalRequirementMail: z.boolean(),
  isEmailSent: z.boolean(),
  sendEmailAgain: z.boolean(),
  //
  days_overdue: z.number().optional(),
  // #1244: Aufgaben-Definitionen der Maßnahme (aus der Rechtsgeschäftsart
  // kopiert, im Vorgang editierbar; materialisieren beim Trigger-Status). Teil
  // des Vorgangs-fullObjects — kein separater Endpoint.
  TaskAutomations: z.array(taskAutomationSchema).optional(),
  // #1244: eigene Wissensergänzung der Maßnahme (editierbar).
  KnowledgeItems: z.array(knowledgeItemSchema).optional(),
  // #1244: aus der Rechtsgeschäftsart geerbte Wissensergänzung (read-only).
  InheritedKnowledgeItems: z.array(knowledgeItemSchema).optional(),
});

export type ILegalRequirement = z.infer<typeof legalRequirementSchema>;

export interface ILegalRequirementFullObject extends ILegalRequirement {
  DeadlineArray?: ILegalRequirementDeadline[];
}

export const isLegalRequirement = (
  requirement: ILegalRequirement | ILegalRequirementTemplate
): requirement is ILegalRequirement => {
  return 'Title' in requirement;
};
